import React from "react";
import {
  TREATMENT_CHECKOUT_COPY,
  TREATMENT_COMPLETION_COPY,
} from "./treatmentUiConstants";
import { productPaymentState } from "./checkoutContract";
import { patientProductName } from "./patientProductName";
import "./treatmentCheckoutJourney.css";
import "./treatmentOrderSubmittedPreview.css";

const money = (value, currency = "USD") => {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch (_error) {
    return `$${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
  }
};

const orderByTreatment = (result) => new Map(
  (result?.orders || []).map((order) => [
    String(order.treatment_type_id || ""),
    order,
  ]),
);

const FAILED_STATUSES = new Set([
  "cancelled",
  "canceled",
  "declined",
  "error",
  "failed",
  "payment_failed",
  "rejected",
]);

const UNKNOWN_PAYMENT_STATUSES = new Set([
  "authorization_unknown",
  "gateway_unknown",
  "payment_status_unknown",
  "unknown",
]);

const SUCCESS_STATUSES = new Set([
  "authorized",
  "captured",
  "complete",
  "completed",
  "delivered",
  "dispatched",
  "fulfilled",
  "not_required",
  "sent",
  "succeeded",
]);

const PRODUCT_PAYMENT_STATE_LABEL = Object.freeze({
  failed: "Payment failed",
  ready: "Payment complete",
  unknown: "Payment status unknown",
  waiting: "Payment pending",
});

const normalizeStatus = (value) => String(value || "pending").trim().toLowerCase();

const humanizeStatus = (value) => String(value || "pending")
  .replace(/[_-]+/g, " ")
  .replace(/\b\w/g, (character) => character.toUpperCase());

const operationState = (value) => {
  const status = normalizeStatus(value);
  if (FAILED_STATUSES.has(status)) return "failed";
  if (UNKNOWN_PAYMENT_STATUSES.has(status)) return "unknown";
  if (SUCCESS_STATUSES.has(status)) return "ready";
  return "waiting";
};

export const treatmentCheckoutState = (order = {}) => {
  const productStates = (order.product_payments || []).map(productPaymentState);
  const childPaymentStatus = normalizeStatus(order.child_payment?.status);
  const states = [
    operationState(childPaymentStatus),
    operationState(order.beluga_dispatch?.status),
    operationState(order.status),
  ];
  if (states.includes("failed") || productStates.includes("failed")) return "failed";
  if (UNKNOWN_PAYMENT_STATUSES.has(childPaymentStatus)) return "unknown";
  if (states.includes("unknown") || productStates.includes("unknown")) return "unknown";
  if (states.includes("waiting") || productStates.includes("waiting")) return "waiting";
  return "ready";
};

const TREATMENT_STATE_LABEL = Object.freeze({
  failed: "Failed",
  ready: "Ready",
  unknown: "Payment Status Unknown",
  waiting: "Waiting",
});

const treatmentOperationRows = (order) => [
  ["Patient payment", order.child_payment?.status],
  ["Provider dispatch", order.beluga_dispatch?.status],
];

const productForPayment = (payment, treatment) => {
  const productId = String(payment?.product_id || "");
  return (treatment?.products || []).find((product) => (
    [product?.id, product?.product_id, product?.productId]
      .filter(Boolean)
      .map(String)
      .includes(productId)
  ));
};

export default function TreatmentOrderSubmitted({
  result,
  summary,
  patientName,
  patientOrdersUrl,
  previewDiscountRate = 0,
}) {
  const preview = Boolean(summary?.preview || result?.preview);
  const currency = summary?.currency || "USD";
  const discountRate = preview
    ? Math.min(1, Math.max(0, Number(previewDiscountRate) || 0))
    : 0;
  const baseTotal = Math.max(0, Number(summary?.grand_total || 0));
  const discountAmount = Number((baseTotal * discountRate).toFixed(2));
  const dueToday = Math.max(
    0,
    Number((baseTotal - discountAmount).toFixed(2)),
  );
  const hasPreviewDiscount = discountAmount > 0;
  const discountPercent = Math.round(discountRate * 100);
  const orders = orderByTreatment(result);
  const treatments = summary?.treatments || [];
  const resultOrders = result?.orders || [];
  const treatmentRows = treatments.length
    ? treatments
    : resultOrders.map((order) => ({
        treatment_type_id: order.treatment_type_id,
        name: order.treatment_type_name || order.treatment_type_key || "Treatment",
        products: [],
        total: order.patient_total,
      }));
  const liveLabOrders = result?.lab_orders || [];
  const previewLabs = preview ? summary?.labs || [] : [];
  const hasNonReadyTreatment = resultOrders.some(
    (order) => treatmentCheckoutState(order) !== "ready",
  );

  return (
    <section
      className={`treatment-confirmation${preview ? " treatment-confirmation--preview" : ""}`}
    >
      <div className="treatment-confirmation__success" aria-hidden="true">✓</div>
      <span className="treatment-confirmation__eyebrow">
        {preview ? "Side-effect-free preview" : "Request received"}
      </span>
      <h1>
        {preview
          ? TREATMENT_COMPLETION_COPY.previewTitle
          : TREATMENT_COMPLETION_COPY.title}
      </h1>
      <p className="treatment-confirmation__intro">
        {preview
          ? TREATMENT_COMPLETION_COPY.previewDescription
          : `${patientName ? `${patientName}, your` : "Your"} care request was submitted. A licensed provider will review each treatment before anything is prescribed or fulfilled.`}
      </p>

      <div className="treatment-confirmation__summary">
        {hasPreviewDiscount ? (
          <div className="treatment-confirmation__summary-stack">
            <div>
              <span>Treatment total</span>
              <strong>{money(baseTotal, currency)}</strong>
            </div>
            <div className="treatment-confirmation__summary-discount">
              <span>{`Discount · ${discountPercent}% off`}</span>
              <strong>−{money(discountAmount, currency)}</strong>
            </div>
            <div className="treatment-confirmation__summary-due">
              <span>{TREATMENT_CHECKOUT_COPY.total}</span>
              <strong>{money(dueToday, currency)}</strong>
            </div>
          </div>
        ) : (
          <div>
            <span>{TREATMENT_COMPLETION_COPY.payment}</span>
            <strong>{money(baseTotal, currency)}</strong>
          </div>
        )}
        {!preview && result?.combined_payment?.status && (
          <small data-testid="combined-payment-status">
            {hasNonReadyTreatment
              ? "Treatment statuses are being reconciled"
              : humanizeStatus(result.combined_payment.status)}
          </small>
        )}
      </div>

      <div className="treatment-confirmation__orders">
        <h2>{TREATMENT_COMPLETION_COPY.orders}</h2>
        {treatmentRows.length > 1 && (
          <p className="treatment-confirmation__orders-note">
            {`You have ${treatmentRows.length} separate treatment orders. Each is reviewed, charged and shipped on its own.`}
          </p>
        )}
        {treatmentRows.map((treatment, index) => {
          const order = orders.get(String(treatment.treatment_type_id || ""));
          const state = order ? treatmentCheckoutState(order) : (preview ? "ready" : "waiting");
          return (
            <article key={treatment.treatment_type_id || treatment.program_id}>
              <div className="treatment-confirmation__order-index" aria-hidden="true">
                {index + 1}
              </div>
              <div className="treatment-confirmation__order-copy">
                <strong>{treatment.name || "Treatment"}</strong>
                <span className="treatment-confirmation__order-id">
                  {order?.order_number
                    ? `Order # ${order.order_number}`
                    : preview
                      ? "Preview treatment order"
                      : "Treatment order pending"}
                </span>
                <small>
                  {(treatment.products || []).map(patientProductName).filter(Boolean).join(", ")}
                </small>
              </div>
              <span
                className={`treatment-confirmation__status is-${state}`}
                data-testid={`treatment-status-${treatment.treatment_type_id || index}`}
              >
                {preview ? "Preview" : TREATMENT_STATE_LABEL[state]}
              </span>
              <strong className="treatment-confirmation__order-total">
                {money(
                  order?.allocated_amount ?? treatment.total ?? order?.patient_total,
                  currency,
                )}
              </strong>
              {!preview && order && (
                <>
                  <dl className="treatment-confirmation__operations">
                    {treatmentOperationRows(order).map(([label, status]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{humanizeStatus(status)}</dd>
                      </div>
                    ))}
                  </dl>
                  {order.product_payments?.length > 0 && (
                    <div
                      className="treatment-confirmation__operations"
                      aria-label="Product payment statuses"
                    >
                      {order.product_payments.map((payment, paymentIndex) => {
                        const state = productPaymentState(payment);
                        const product = productForPayment(payment, treatment);
                        const name = patientProductName(product)
                          || payment.product_name
                          || `Product ${paymentIndex + 1}`;
                        const detail = state === "unknown"
                          ? "Do not resubmit; payment outcome is being reconciled."
                          : state === "failed"
                            ? payment.patient_action === "contact_support"
                              ? "Payment failed for this product. Contact support before retrying."
                              : "Payment failed for this product."
                            : state === "waiting"
                              ? "Payment is still processing."
                              : PRODUCT_PAYMENT_STATE_LABEL[state];
                        return (
                          <div
                            key={payment.reservation_id || payment.line_item_id || paymentIndex}
                            data-testid={`product-payment-${payment.reservation_id || paymentIndex}`}
                          >
                            <dt>{name}</dt>
                            <dd>
                              {detail}
                              {payment.amount ? ` · ${money(payment.amount, payment.currency || currency)}` : ""}
                            </dd>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </article>
          );
        })}
      </div>

      {(liveLabOrders.length > 0 || previewLabs.length > 0) && (
        <div className="treatment-confirmation__labs">
          <h2>{TREATMENT_COMPLETION_COPY.labs}</h2>
          {previewLabs.map((lab) => (
            <div key={lab.id || lab.name}>
              <span>{lab.name}</span>
              <strong>{money(lab.line_total, currency)}</strong>
            </div>
          ))}
          {liveLabOrders.map((labOrder) => (
            <div key={labOrder.lab_order_id}>
              <span>
                <strong>{labOrder.panel_name || "Lab panel"}</strong>
                <small>
                  {labOrder.order_number
                    ? `Order ${labOrder.order_number}`
                    : `Order ${labOrder.lab_order_id}`}
                </small>
              </span>
              <strong>
                {String(labOrder.status || "submitted").replace(/_/g, " ")}
                {" · "}
                {money(labOrder.captured_amount, currency)}
              </strong>
            </div>
          ))}
        </div>
      )}

      <div className="treatment-confirmation__next" role="note">
        <span aria-hidden="true">i</span>
        <div>
          <strong>What happens next</strong>
          <p>
            {preview
              ? TREATMENT_COMPLETION_COPY.previewNext
              : TREATMENT_COMPLETION_COPY.next}
          </p>
          <ol>
            {TREATMENT_COMPLETION_COPY.nextSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      </div>
      <div className="treatment-confirmation__important" role="note">
        <strong>Important</strong>
        <p>{TREATMENT_COMPLETION_COPY.important}</p>
      </div>
      {preview ? (
        <button
          type="button"
          className="treatment-confirmation__orders-action"
          disabled
          title="Preview mode does not create patient orders"
        >
          {TREATMENT_COMPLETION_COPY.viewOrders}
        </button>
      ) : patientOrdersUrl ? (
        <a
          className="treatment-confirmation__orders-action"
          href={patientOrdersUrl}
        >
          {TREATMENT_COMPLETION_COPY.viewOrders}
        </a>
      ) : null}
    </section>
  );
}
